import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { reactive, ref, watch, onMounted, onUnmounted, h } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { MenuProps } from "ant-design-vue";
import {
  PieChartOutlined,
  UserOutlined,
  TeamOutlined,
  FileOutlined,
  SafetyOutlined,
  DollarOutlined,
  IdcardOutlined,
  CarryOutOutlined,
  DatabaseOutlined,
  MailOutlined,
  WalletOutlined,
  HomeOutlined,
  CopyrightOutlined,
  AccountBookOutlined,
} from "@ant-design/icons-vue";
import { getMenuList } from "@/utils/auth";

interface MenuItemType {
  key: string;
  icon?: () => ReturnType<typeof h>;
  children?: MenuItemType[];
  label: string;
  type?: "group" | "divider";
}

interface MenuDataItem {
  id: number;
  permissionName: string;
  menuCode: string;
  path: string | null;
  type: number;
  isHide?: number;
  children?: MenuDataItem[];
}

// Props 定义
// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'Sider',
  props: {
  collapsed: {
    type: Boolean,
    default: false,
  },
  isH5: {
    type: Boolean,
    default: false,
  },
},
  emits: ["close"],
  setup(__props, { emit }) {

const props = __props;



// Emits 定义
// eslint-disable-next-line no-undef


const route = useRoute();
const router = useRouter();

const selectedKeys = ref<string[]>([]);
const openKeys = ref<string[]>([]);
const preOpenKeys = ref<string[]>([]);

// 图标映射
const iconMap: Record<string, () => ReturnType<typeof h>> = {
  "/user": () => h(CopyrightOutlined),
  "/accounts": () => h(IdcardOutlined),
  "/paylist": () => h(DollarOutlined),
  "/agentlist": () => h(TeamOutlined),
  "/contactlist": () => h(UserOutlined),
  "/worklist": () => h(FileOutlined),
  "/bm": () => h(IdcardOutlined),
  "/RoleManage": () => h(SafetyOutlined),
  "/rolelist": () => h(SafetyOutlined),
  "/addata": () => h(DatabaseOutlined),
  "/backlog": () => h(CarryOutOutlined),
  "/noticelist": () => h(MailOutlined),
  "/walletlist": () => h(WalletOutlined),
  "/home": () => h(HomeOutlined),
  "/expend": () => h(AccountBookOutlined),
};

// 根据路径获取图标
function getIconByPath(path: string): (() => ReturnType<typeof h>) | undefined {
  return iconMap[path] || iconMap["/user"]; // 默认使用用户管理图标
}

// 将后端菜单数据转换为 Ant Design Vue 菜单格式
function convertMenuData(menuData: MenuDataItem[]): MenuItemType[] {
  return menuData
    .map((item) => {
      const menuItem: MenuItemType = {
        key: item.path || `/${item.id}`,
        label: item.permissionName,
        icon: item.path ? getIconByPath(item.path) : undefined,
      };

      // 如果有子菜单，递归转换并过滤掉 isHide === 1 的项
      if (item.children && item.children.length > 0) {
        // 过滤掉 isHide === 1 的子菜单项
        const filteredChildren = item.children.filter(
          (child) => child.isHide !== 1
        );
        if (filteredChildren.length > 0) {
          menuItem.children = convertMenuData(filteredChildren);
        }
      }

      return menuItem;
    })
    .filter((item) => item !== null);
}

// 菜单项配置
const items: MenuItemType[] = reactive([]);

// 初始化菜单
function initMenu() {
  const menuList = getMenuList();
  console.log(menuList);
  if (menuList && Array.isArray(menuList)) {
    const convertedItems = convertMenuData(menuList);
    console.log(convertedItems);
    items.splice(0, items.length, ...convertedItems); 
  } else {
    // 如果没有菜单数据，使用默认菜单
    items.splice(0, items.length, ...[
      {
        key: "/user",
        label: "用户管理",
        icon: () => h(PieChartOutlined),
      },
    ]);
  }
}

// 监听 openKeys 变化，保存 preOpenKeys
watch(openKeys, (_val, oldVal) => {
  preOpenKeys.value = oldVal;
});

// 监听 collapsed 变化，处理 openKeys
watch(
  () => props.collapsed,
  (collapsed) => {
    openKeys.value = collapsed ? [] : preOpenKeys.value;
  }
);

// 更新选中项和展开项
function updateSelectedKeys() {
  selectedKeys.value = [route.path];
  // 如果当前路径是子菜单，自动展开父菜单
  const currentItem = items.find((item) => {
    if (item.children) {
      return item.children.some((child) => child.key === route.path);
    }
    return item.key === route.path;
  });
  if (currentItem?.children) {
    openKeys.value = [currentItem.key];
    preOpenKeys.value = [currentItem.key];
  }
}

// 使用定时器定期检查菜单数据是否有更新（用于刷新时检测菜单变化）
let menuCheckTimer: ReturnType<typeof setInterval> | null = null;
let lastMenuDataHash = "";

// 计算菜单数据的哈希值，用于检测菜单是否有变化
function getMenuDataHash(menuList: any[]): string {
  if (!menuList || !Array.isArray(menuList)) return "";
  return JSON.stringify(menuList.map(m => ({ id: m.id, name: m.permissionName, path: m.path })));
}

// 初始化选中项
onMounted(() => {
  initMenu();
  updateSelectedKeys();
  
  // 初始化菜单数据哈希
  const menuList = getMenuList();
  if (menuList && Array.isArray(menuList)) {
    lastMenuDataHash = getMenuDataHash(menuList);
  }
  
  // 延迟检查菜单数据，防止首次加载时数据还未保存
  setTimeout(() => {
    const menuList = getMenuList();
    if (menuList && Array.isArray(menuList) && items.length === 0) {
      initMenu();
      updateSelectedKeys();
    }
  }, 100);

  // 每500ms检查一次菜单数据是否有变化（用于检测刷新后的菜单更新）
  menuCheckTimer = setInterval(() => {
    const menuList = getMenuList();
    if (menuList && Array.isArray(menuList)) {
      const currentHash = getMenuDataHash(menuList);
      // 如果菜单数据哈希值发生变化，说明菜单已更新，重新初始化
      if (currentHash && currentHash !== lastMenuDataHash) {
        lastMenuDataHash = currentHash;
        initMenu();
        updateSelectedKeys();
      }
    }
  }, 500);
});

// 监听路由变化，更新选中项
watch(
  () => route.path,
  () => {
    updateSelectedKeys();
  },
  { immediate: true }
);

// 组件卸载时清除定时器
onUnmounted(() => {
  if (menuCheckTimer) {
    clearInterval(menuCheckTimer);
    menuCheckTimer = null;
  }
});

// 菜单点击事件
const handleClick: MenuProps["onClick"] = (info) => {
  router.push(info.key as string);
  emit("close");
};

return (_ctx: any,_cache: any) => {
  const _component_a_menu = _resolveComponent("a-menu")!

  return (_openBlock(), _createBlock(_component_a_menu, {
    openKeys: openKeys.value,
    "onUpdate:openKeys": _cache[0] || (_cache[0] = ($event: any) => ((openKeys).value = $event)),
    selectedKeys: selectedKeys.value,
    "onUpdate:selectedKeys": _cache[1] || (_cache[1] = ($event: any) => ((selectedKeys).value = $event)),
    theme: "light",
    mode: "inline",
    "inline-collapsed": __props.collapsed,
    items: items,
    onClick: handleClick
  }, null, 8, ["openKeys", "selectedKeys", "inline-collapsed", "items"]))
}
}

})